import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '../hooks/useAuth';
import { AuthApiError } from '../services/auth-api';

export function MfaVerifyPage() {
  const { verifyMfa } = useAuth();
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!code || code.length !== 6 || !/^\d{6}$/.test(code)) {
      setError('Insira um código de 6 digitos.');
      return;
    }

    const sessionToken = sessionStorage.getItem('gp_mfa_session') || '';
    if (!sessionToken) {
      setError('Sessao expirada. Faca login novamente.');
      return;
    }

    setIsSubmitting(true);
    try {
      await verifyMfa({ code, sessionToken });
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.statusCode === 401) {
          setError('Codigo inválido. Tente novamente.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Erro na verificação. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" gutterBottom align="center" fontWeight={700}>
            Verificação MFA
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Insira o código de 6 digitos do seu aplicativo autenticador.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              label="Codigo TOTP"
              fullWidth
              margin="normal"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
              autoFocus
              disabled={isSubmitting}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={isSubmitting}
              sx={{ mt: 2 }}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'Verificar'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
